<template>
  <div
    id="product-form"
    class="product-form"
    :class="{ [$class('loading')]: loading }"
    data-sign-post-location="main"
    js-sign-post
  >
    <form
      class="product-form__form form-group"
      :class="{ [$class('error')]: error }"
      :disabled="isOutOfStock"
      @submit.prevent="handleAddToCart"
    >
      <product-options
        v-show="showProductOptions"
        class="product-form__options"
        :loading="loading"
        :product="product"
        :section="section"
        :variant="variant"
        @update-variant="handleUpdateVariant"
      />

      <input
        name="quantity"
        readonly
        type="hidden"
        :value="quantity"
      >

      <div class="form-group__field">
        <div
          class="product-form__add-to-cart-container"
          :class="{ [$class('active')]: isFloating }"
        />

        <btn
          ref="addToCartButton"
          class="product-form__add-to-cart"
          :class="{ [$class('fixed')]: isFloating }"
          :disabled="isOutOfStock"
          :label="addToCartText"
          modifiers="block center yellow"
          :show-state-icon="state === 'adding'"
          type="submit"
        >
          <template #stateIcon>
            <icon-loading />
          </template>
        </btn>

        <p
          v-show="error"
          class="form-group__note text-body-s-regular"
          role="status"
          v-text="error"
        />
      </div>

      <recipient-form
        v-if="product.giftCard"
        class="product-form__recipient-form"
        :gift-card="giftCard"
        :section="section"
        @update-recipient-form="handleUpdateRecipientForm"
      />
    </form>

    <div
      v-if="isDigital"
      class="product-form__preview product-form__preview--desktop"
    >
      <btn
        :label="$string('product.preview.desktop')"
        modifiers="block center secondary"
        type="button"
        @click.prevent="handlePreviewLink"
      />
    </div>

    <div
      v-if="isDigital"
      class="product-form__preview product-form__preview--mobile"
    >
      <btn
        :label="$string('product.preview.mobile')"
        modifiers="block center secondary"
        type="button"
        @click.prevent="handlePreviewLink"
      />
    </div>

    <p
      v-show="previewLinkError"
      class="product-form__preview-error text-body-s-regular"
      v-text="$string('product.preview.error')"
    />

    <div ref="observerElement" />
  </div>
</template>

<script>

/**
 * Vue: Product form (product-form)
 * -----------------------------------------------------------------------------
 * Product form to handle variant selection and adding to cart.
 *
 * @param {Object} [autofixCategories] - Vehicle type to autofix category
 * mapping from theme settings.
 * @param {Boolean} [loading] - Loading state.
 * @param {Boolean|Object} product - Canvas formatted product object.
 * @param {Object} section - Main product section settings.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 *
 * @emits updateVariant - Emit new variant object.
 *
 */
import { mapActions, mapGetters } from 'vuex'

import Btn from '~global/btn/btn'
import ProductOptions from '~async/product-form/product-options'
import RecipientForm from '~async/product-form/recipient-form'

import IconLoading from '~icons/general/loading.svg'

import {
  formatProductForBloomreachreach,
  trackBloomreachEvent,
} from '~/helpers/bloomreach'
import { setIntersectionObserver } from '~/helpers/general'
import { isDigitalVariant } from '~/helpers/product'
import { emitUpdateVariant } from '~/helpers/vue'

export default {
  name: 'ProductForm',

  components: {
    Btn,
    IconLoading,
    ProductOptions,
    RecipientForm,
  },

  props: {
    autofixCategories: {
      type: Object,
      default: () => ({
        axles: [],
        cars: [],
        motorcycles: [],
        trailers: [],
        trucks: [],
      }),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    section: {
      type: Object,
      default: () => ({
        autoOpenCartDrawer: true,
        displayRecipientForm: true,
        displaySingleOptionNames: true,
        displaySingleValueOptions: false,
        displaySingleVariantOption: false,
        dropdownOptions: [],
        excludedInitialSelectOptions: ['length', 'size', 'waist', 'width'],
        hideGiftCardOptionNames: true,
        id: '',
        initialSelect: true,
        resetFormBehaviour: 'disabled',
        resetRecipientFormOnAdd: true,
        selectDisabledValues: true,
      }),
      required: true,
    },
    variant: {
      type: [Boolean, null, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'update-variant': emitUpdateVariant,
  },

  data() {
    return {
      error: '',
      giftCard: {
        enableForm: false,
        fields: {
          Message: '',
          'Recipient email': '',
          'Recipient name': '',
          'Send on': '',
        },
        timezoneOffset: '',
      },
      lineItemProperties: {},
      previewLinkError: false,
      quantity: 1,
      state: 'ready',
      timeout: {},
      observer: null,
      isFloating: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (!this.variant) {
        return this.product.giftCard
          ? this.$string('product.cta.select_gift')
          : this.$string('product.cta.select_variant')
      }

      if (this.isOutOfStock) {
        return this.$string('product.cta.sold_out')
      }

      return this.state === 'added'
        ? this.$string('product.cta.added_to_cart')
        : this.$string('product.cta.add_to_cart')
    },

    /**
     * Compute autofix vehicle category.
     * @returns {String}
     */
    autofixVehicleCategory() {
      const vehicleType = this.product?.metafields?.vehicleType?.[0]

      if (this.autofixCategories.cars.includes(vehicleType)) {
        return 'cars'
      }

      if (this.autofixCategories.motorcycles.includes(vehicleType)) {
        return 'motorcycles'
      }

      if (this.autofixCategories.trucks.includes(vehicleType)) {
        return 'trucks'
      }

      if (this.autofixCategories.trailers.includes(vehicleType)) {
        return 'trailers'
      }

      if (this.autofixCategories.axles.includes(vehicleType)) {
        return 'axles'
      }

      return 'cars'
    },

    /**
     * Compute gift card line item properties.
     * @returns {Null|Object}
     */
    giftCardLineItemProperties() {
      if (
        !this.product.giftCard ||
        this.giftCard.fields['Recipient email'] === ''
      ) {
        return null
      }

      const filteredFields = {}

      Object.entries(this.giftCard.fields).forEach(([key, value]) => {
        if (!value) {
          return
        }

        filteredFields[key] = value
      })

      return {
        __shopify_send_gift_card_to_recipient: 'on',
        __shopify_offset: this.giftCard.timezoneOffset,
        ...filteredFields,
      }
    },

    /**
     * Return if the current variant is digital.
     * @returns {Boolean}
     */
    isDigital() {
      return isDigitalVariant(this.variant)
    },

    /**
     * Return if the current variant is out of stock.
     * @returns {Boolean}
     */
    isOutOfStock() {
      return !this.variant?.available
    },

    /**
     * Compute show product form element.
     * @returns {Boolean}
     */
    showProductOptions() {
      if (this.loading && !this.product) {
        return true
      }

      const multipleOptions = this.product?.options?.some((option) => {
        return option.values.length > 1
      })

      /**
       * If multiple variants then ensure they have more than one option.
       */
      const showOptionsMultiVariant =
        !this.product.hasOnlyDefaultVariant &&
        multipleOptions

      /**
       * If single variant then check if section settings enable display.
       */
      const showOptionsSingleVariant =
        this.section.displaySingleValueOptions &&
        this.section.displaySingleVariantOption

      return showOptionsMultiVariant || showOptionsSingleVariant
    },
  },

  watch: {

    /**
     * Watch product to reset error.
     */
    product() {
      this.error = ''
      this.previewLinkError = false
    },

    /**
     * Watch variant to reset error.
     */
    variant() {
      this.error = ''
      this.previewLinkError = false
    },
  },

  beforeMount() {
    this.giftCard.timezoneOffset = new Date().getTimezoneOffset().toString()
  },

  mounted() {
    this.setIntersectionObserver()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      if (!this.variant) {
        this.error = this.product.giftCard
          ? this.$string('product.form.select_gift')
          : this.$string('product.form.select_variant')
        return
      }

      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      if (this.$variable('storybook')) {
        if (!this.variant.available) {
          this.error = this.$string('product.form.sold_out')
          this.state = 'ready'
          return
        }

        this.timeout = setTimeout(() => {
          this.state = 'added'
        }, this.$timing('slow'))

        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, this.$timing('message'))

        return
      }

      this.setLineItemProperties()

      const items = [
        {
          id: this.variant.id,
          properties: this.product.giftCard
            ? {
              ...this.giftCardLineItemProperties,
              ...this.lineItemProperties,
            }
            : this.lineItemProperties,
          quantity: this.quantity,
        },
      ]

      try {
        await this.addItem(items)
        this.state = 'added'

        /**
         * Bloomreach tracking events.
         * - `cart_update` is used to trigger a wider range of scenarios.
         */
        trackBloomreachEvent(
          'add item to cart',
          formatProductForBloomreachreach(this.product, this.variant),
        )

        if (
          this.section.autoOpenCartDrawer &&
          this.getComponentExists('cart-drawer')
        ) {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          })
        }

        await this.resetState()

      } catch (error) {
        this.state = 'error'

        if (error.description) {
          this.handleErrorMessage(error.description)
          return
        }

        cnvs.ReportError('Failed to add product to cart', error)
      }
    },

    /**
     * Set product line items.
     */
    setLineItemProperties() {
      if (this.product.giftCard || !this.variant) {
        return
      }

      if (this.variant.metafields.remoteContentType) {
        // eslint-disable-next-line no-underscore-dangle
        this.lineItemProperties._product_format = this.variant.metafields.remoteContentType
        return
      }

      const formatOption = this.variant.selectedOptions.find((option) => {
        return option.name === 'Type' || option.name === 'Title'
      })

      if (!formatOption) {
        return
      }

      // eslint-disable-next-line no-underscore-dangle
      this.lineItemProperties._product_format = formatOption.value
    },

    /**
     * Reset product form state after adding to cart after message delay.
     */
    async resetState() {
      await cnvs.Sleep(this.$timing('message'))
      this.state = 'ready'

      /**
       * Reset gift card recipient form based on setting.
       */
      if (this.product.giftCard && this.section.resetRecipientFormOnAdd) {
        this.giftCard = {
          enableForm: false,
          fields: {
            Message: '',
            'Recipient email': '',
            'Recipient name': '',
            'Send on': '',
          },
        }
      }

      /**
       * Reset variant selection based on setting.
       */
      if (this.section.resetFormBehaviour !== 'clear') {
        return
      }

      this.handleUpdateVariant(false)
    },

    /**
     * Handle update variant event.
     * @param {Boolean|Null|Object} variant - Emitted variant.
     */
    handleUpdateVariant(variant) {
      this.$emit('update-variant', variant)
    },

    /**
     * Handle update recipient form event.
     * @param {Object} recipientForm - Emitted recipient form.
     */
    handleUpdateRecipientForm(recipientForm) {
      this.giftCard = recipientForm
    },

    /**
     * Handle error message.
     * @param {Object} errorDescription - Error description from add request.
     */
    handleErrorMessage(errorDescription) {
      if (typeof errorDescription === 'string') {
        this.error = errorDescription
        return
      }

      this.error = Object.values(errorDescription)
        .flat()
        .map((error) => {
          return error.endsWith('.') ? error : `${error}.`
        })
        .join('<br>')
    },

    /**
     * Handle preview link interaction.
     */
    async handlePreviewLink() {
      try {
        const response = await this.requestRemotePreviewLink()
        this.previewLinkError = false
        const windowReference = window.open()
        windowReference.location = response.response
        windowReference.target = '_blank'

      } catch (error) {
        this.previewLinkError = true
        cnvs.ReportError('Failed to get Remote Content Preview Link', error.message)
      }
    },

    /**
     * Request remote content preview link.
     * @returns {Promise}
     */
    async requestRemotePreviewLink() {
      const vehicleManagerApi = this.$variable('credentials.customApis.vehicleManager')
      let remoteContentId = this.variant.metafields.remoteContentId

      if (this.variant.metafields.remoteContentType === 'autofix') {
        remoteContentId = this.variant.metafields.remoteContentId.split(':')[0]
      }

      const parameters = [
        `type=${this.variant.metafields.remoteContentType}`,
        `remoteContentId=${remoteContentId}`,
        `vehicleCategory=${this.autofixVehicleCategory}`,
      ]

      const url = `${vehicleManagerApi}/get-remote-preview-content-link?${parameters.join('&')}`

      let response = await fetch(url)
      response = await response.json()

      return new Promise((resolve, reject) => {
        if (response.error) {
          reject(response)
        } else {
          resolve(response)
        }
      })
    },

    /**
     * Set IntersectionObserver to observe the observer element.
     */
    setIntersectionObserver() {
      const observerElement = this.$refs.observerElement
      const addToCartButton = this.$refs.addToCartButton

      if (
        !observerElement ||
        !addToCartButton ||
        'IntersectionObserver' in window === false
      ) {
        return
      }

      setIntersectionObserver({
        callback: this.handleIntersection,
        element: observerElement,
        rootMargin: '0px',
        threshold: 0,
      })
    },

    /**
     * Handle intersection.
     * @param {Array} entries - Intersection entries.
     */
    handleIntersection(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting || entry.boundingClientRect.top > 0) {
          this.isFloating = false
          return
        }

        this.isFloating = true
      })
    },
  },
}
</script>

<style lang="scss">
@import './product-form';
</style>
